import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Package, Clock, Hash, ExternalLink } from 'lucide-react';

import InboxHeader       from '../../components/Inbox/InboxHeader';
import ActionButtons     from '../../components/Inbox/ActionButtons';
import RemarksHistory    from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout  from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchItemCodeInbox,
    fetchItemCodeDetail,
    fetchItemCodeTraders,
    fetchItemCodeLinks,
    fetchItemCodeRemarks,
    submitItemCodeVerification,
    clearDetail,
    clearApprovalResult,
    resetAll,
    selectItemCodeInbox,
    selectItemCodeDetail,
    selectItemCodeTraders,
    selectItemCodeLinks,
    selectItemCodeRemarks,
    selectICLoading,
    selectICErrors,
} from '../../slices/purchaseSlice/itemCodeVerificationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

// ── Layout helpers (legacy VerifyItemCodeCreationView arrangement) ────────────

const inputCls = 'w-full px-2.5 py-1.5 text-sm border rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';
const roCls    = `${inputCls} border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40`;
const editCls  = `${inputCls} border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-800`;

const SectionTitle = ({ children }) => (
    <h3 className="col-span-12 text-xs font-bold uppercase tracking-wide text-blue-900 dark:text-blue-300 border-b-2 border-blue-900/70 dark:border-blue-500 pb-1 mt-1">
        {children}
    </h3>
);

const FieldLabel = ({ children, required }) => (
    <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
        {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
);

const RoField = ({ label, value, span = 'md:col-span-4', title }) => (
    <div className={`col-span-12 ${span}`}>
        <FieldLabel>{label}</FieldLabel>
        <input type="text" readOnly value={value || ''} title={title || value || ''} className={roCls} />
    </div>
);

const thCls = 'px-2 py-1.5 text-[11px] font-semibold text-white bg-blue-900 dark:bg-blue-800';

// Legacy only renders a link row when both the link and its rate are present
const hasLink = (lk) => !!(lk?.Link && String(lk.Link).trim()) && lk?.LinkRate != null && String(lk.LinkRate).trim() !== '';

// Stored links are often bare ("amazon.in/..."); without a scheme the browser resolves them
// relative to this app, so prefix https:// to make them open the actual site.
const toHref = (url) => {
    const u = String(url || '').trim();
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(u) ? u : `https://${u}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const VerifyItemCode = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    const inbox          = useSelector(selectItemCodeInbox);
    const detail         = useSelector(selectItemCodeDetail);
    const traders        = useSelector(selectItemCodeTraders);
    const links          = useSelector(selectItemCodeLinks);
    const remarks        = useSelector(selectItemCodeRemarks);
    const loading        = useSelector(selectICLoading);
    const errors         = useSelector(selectICErrors);

    const statusLoading  = useSelector(selectStatusListLoading);
    const statusError    = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions     = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId   = userData?.roleId || userData?.RID;
    const userName = userData?.userName || userDetails?.userName || 'system';

    // Local UI state
    const [selectedItem,         setSelectedItem]         = useState(null);
    const [isVerified,           setIsVerified]           = useState(false);
    const [verificationComment,  setVerificationComment]  = useState('');
    const [showRemarksHistory,   setShowRemarksHistory]   = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered,   setIsLeftPanelHovered]   = useState(false);
    // Legacy requires every trader row and every link row to be ticked before submit
    const [checkedTraders,       setCheckedTraders]       = useState({});
    const [checkedLinks,         setCheckedLinks]         = useState({});

    // Editable fields — verifier can update these
    const [editBasicprice,   setEditBasicprice]   = useState('');
    const [editItemname,     setEditItemname]     = useState('');
    const [editSpecification, setEditSpecification] = useState('');

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const getCurrentRoleName = () =>
        userDetails?.roleName || userData?.roleName ||
        notificationData?.InboxTitle || 'Item Code Verifier';

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (roleId) dispatch(fetchItemCodeInbox(roleId));
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetAll());
            dispatch(resetApprovalData());
        };
    }, [roleId, dispatch]);

    // Fetch detail when item selected
    useEffect(() => {
        if (!selectedItem) return;
        dispatch(fetchItemCodeDetail(selectedItem.Rowid));
        setIsVerified(false);
        setVerificationComment('');
        setShowRemarksHistory(false);
        setCheckedTraders({});
        setCheckedLinks({});
        dispatch(clearDetail());
    }, [selectedItem, dispatch]);

    // After detail loads — fetch supplementary data and populate editable fields
    useEffect(() => {
        if (!detail || !roleId) return;

        const moid  = detail.MOID || 270;
        const tranNo = detail.TranNo || '';

        dispatch(fetchStatusList({ MOID: moid, ROID: roleId, ChkAmt: 0 }));

        if (tranNo) {
            dispatch(fetchItemCodeTraders(tranNo));
            dispatch(fetchItemCodeLinks(tranNo));
            dispatch(fetchItemCodeRemarks(tranNo));
        }

        setEditBasicprice(detail.Basicprice || '');
        setEditItemname(detail.Itemname || '');
        setEditSpecification(detail.Specification || '');
    }, [detail, roleId, dispatch]);

    // Collapse left panel when item selected
    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleRefresh = () => {
        if (roleId) dispatch(fetchItemCodeInbox(roleId));
    };

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const buildPayload = (actionValue) => {
        const roleName = getCurrentRoleName();
        const existingRemarks = detail?.Remarks || '';
        const formatted = `${roleName} : ${userName} : ${verificationComment.trim()}`;
        const updatedRemarks = existingRemarks.trim()
            ? `${existingRemarks.trim()}||${formatted}`
            : formatted;

        return {
            Rowid:         detail?.Rowid || selectedItem?.Rowid || '',
            Remarks:       verificationComment.trim(), // SP builds the 'Action : Role : User : note |' history itself; legacy sends the note only,
            Appstatus:     actionValue,
            RoleID:        roleId,
            LastRoleID:    detail?.LastRoleID || null,
            Basicprice:    editBasicprice,
            Itemname:      editItemname,
            Specification: editSpecification,
            Createdby:     userName,
        };
    };

    const visibleLinks = links.filter(hasLink);

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No item code selected.'); return; }
        if (!editItemname.trim())      { toast.error('Enter Item Name.');      return; }
        if (!(parseFloat(editBasicprice) > 0)) { toast.error('Invalid Basic Price.'); return; }
        if (!editSpecification.trim()) { toast.error('Enter Specification.');  return; }
        if (traders.some((_, i) => !checkedTraders[i])) {
            toast.error('Please verify all trader details (tick each row).');
            return;
        }
        if (visibleLinks.some((_, i) => !checkedLinks[i])) {
            toast.error('Please verify all link data (tick each row).');
            return;
        }
        if (!verificationComment.trim()) {
            toast.error('Verification comment is mandatory.');
            return;
        }
        if (!isVerified) {
            toast.error('Please check the verification checkbox before proceeding.');
            return;
        }

        let actionValue = action.value || action.text || action.type;
        if (!actionValue?.trim()) {
            const map = { approve: 'Approve', verify: 'Verify', reject: 'Reject', return: 'Return' };
            actionValue = map[action.type?.toLowerCase()] || 'Verify';
        }

        try {
            const result = await dispatch(submitItemCodeVerification(buildPayload(actionValue))).unwrap();
            // spVerifyItemCode returns 'Submitted' or an error text (HTTP 200 either way).
            const statusText = String(result?.Data ?? result ?? '').trim();
            if (statusText.split('$')[0] !== 'Submitted') {
                toast.error(statusText || 'Action was not applied', { autoClose: 10000 });
                return;
            }
            const msg = typeof result === 'string' ? result : JSON.stringify(result);

            // Backend returns "Invalid ..." strings in Data even when IsSuccessful: true
            if (typeof result === 'string' && result.toLowerCase().startsWith('invalid')) {
                toast.error(result, { autoClose: 10000 });
                return;
            }

            toast.success(`${action.text || actionValue} completed successfully!`);
            if (msg.includes('$')) {
                const info = msg.split('$')[1];
                if (info) setTimeout(() => toast.info(info, { autoClose: 6000 }), 500);
            }
            setTimeout(() => {
                dispatch(fetchItemCodeInbox(roleId));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(clearDetail());
                dispatch(resetApprovalData());
                dispatch(clearApprovalResult());
            }, 1000);
        } catch (err) {
            const msg = typeof err === 'string' ? err : err?.message || `Failed to ${actionValue.toLowerCase()}`;
            toast.error(msg, { autoClose: 10000 });
        }
    };

    // ── Filtered inbox ────────────────────────────────────────────────────────

    const filteredItems = inbox.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.Itemname?.toLowerCase().includes(q) ||
            item.ItemCode?.toLowerCase().includes(q) ||
            item.Rowid?.toString().includes(q)
        );
    });

    // ── Left panel renderers ──────────────────────────────────────────────────

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                    <div className="w-11 h-11 rounded-full border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/40 dark:to-blue-800/40 flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-800 dark:text-blue-400" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {item.Itemname || '—'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">
                        {item.ItemCode}
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-1 text-xs">
                {item.Basicprice && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                        ₹ {item.Basicprice}
                    </span>
                )}
                <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                    <Hash className="w-3 h-3" />{item.Rowid}
                </span>
            </div>
        </div>
    );

    // Compact single-line row for the "classic" list view — same fields as
    // renderItemCard, laid out horizontally instead of stacked.
    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">
                {item.Itemname || '—'}
            </span>
            <span className="font-mono text-gray-500 dark:text-gray-400 min-w-[110px]">
                {item.ItemCode}
            </span>
            {item.Basicprice && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                    ₹ {item.Basicprice}
                </span>
            )}
            <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                <Hash className="w-3 h-3" />{item.Rowid}
            </span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/40 dark:to-blue-800/40 flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-800 dark:text-blue-400" />
        </div>
    );

    // ── Detail panel ──────────────────────────────────────────────────────────

    const allTradersChecked = traders.length > 0 && traders.every((_, i) => checkedTraders[i]);
    const allLinksChecked   = visibleLinks.length > 0 && visibleLinks.every((_, i) => checkedLinks[i]);
    const toggleAll = (rows, allChecked, setter) =>
        setter(allChecked ? {} : Object.fromEntries(rows.map((_, i) => [i, true])));

    const renderDetailContent = (isPopup = false) => {
        if (!selectedItem) return null;

        const priceOver = parseFloat(editBasicprice) > parseFloat(detail?.Basicprice || 0);

        return (
            <div className="space-y-3">
                {loading.detail && (
                    <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 border border-blue-200 dark:border-blue-700">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-900" />
                        <span className="text-sm text-blue-900 dark:text-blue-400">Loading item code details...</span>
                    </div>
                )}

                {/* Slim header */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
                    <Package className="w-4 h-4 text-blue-900 dark:text-blue-300 shrink-0" />
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {detail?.Itemname || selectedItem?.Itemname}
                    </span>
                    <span className="text-xs font-mono text-blue-800 dark:text-blue-400">
                        {detail?.ItemCode || selectedItem?.ItemCode}
                        {detail?.TranNo && ` · Txn: ${detail.TranNo}`}
                    </span>
                </div>

                {detail && (
                    <div className="grid grid-cols-12 gap-x-3 gap-y-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        {/* Item Category */}
                        <SectionTitle>Item Category</SectionTitle>
                        <RoField label="Item Code" value={detail.ItemCode} />
                        <div className="col-span-12 md:col-span-4">
                            <FieldLabel required>Item Name</FieldLabel>
                            <input type="text" value={editItemname} onChange={(e) => setEditItemname(e.target.value)} className={editCls} />
                        </div>
                        <div className="col-span-12 md:col-span-4">
                            <FieldLabel required>
                                Basic Price
                                {detail.Basicprice && <span className="ml-1 text-gray-400 font-normal">(max ₹{detail.Basicprice})</span>}
                            </FieldLabel>
                            <input
                                type="number"
                                value={editBasicprice}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const original = parseFloat(detail?.Basicprice || 0);
                                    if (val !== '' && parseFloat(val) > original) return;
                                    setEditBasicprice(val);
                                }}
                                max={detail.Basicprice || undefined}
                                min={0}
                                className={`${editCls} ${priceOver ? 'border-red-400 dark:border-red-500' : ''}`}
                            />
                        </div>

                        <RoField label="Item Code Type" value={detail.ItemCodeType}    span="md:col-span-2" />
                        <RoField label="Category Name"  value={detail.TransactionType} span="md:col-span-8" />
                        <RoField label="HSN Code"       value={detail.HSNCode}         span="md:col-span-2" title={detail.HSNRemarks} />

                        <RoField label="DCA Head"     value={detail.ItemcodeDca}  span="md:col-span-6" />
                        <RoField label="Sub DCA Head" value={detail.ItemcodeSDca} span="md:col-span-6" />

                        {detail.ConnectedTanNo && (
                            <>
                                <RoField label="Connected Item Code" value={detail.ConnectedItemcode} />
                                <RoField label="Connected DCA"       value={detail.ConnectedDCA} />
                                <RoField label="Connected SDCA"      value={detail.ConnectedSDca} />
                            </>
                        )}

                        {/* Group Code */}
                        <SectionTitle>Group Code</SectionTitle>
                        <RoField label="Major Group Code / Name" value={detail.Majorgroupcode ? `${detail.Majorgroupcode}${detail.Majorgroupname ? ' — ' + detail.Majorgroupname : ''}` : ''} />
                        <RoField label="Sub-Group Code"          value={detail.Subgroupcode ? `${detail.Subgroupcode}${detail.Subgroupname ? ' — ' + detail.Subgroupname : ''}` : ''} />
                        <RoField label="Specification Code"      value={detail.Specificationcode} />

                        {/* Specification */}
                        <SectionTitle>Specification</SectionTitle>
                        <div className="col-span-12 md:col-span-8">
                            <FieldLabel required>Specification</FieldLabel>
                            <textarea rows={2} value={editSpecification} onChange={(e) => setEditSpecification(e.target.value)} className={`${editCls} resize-y`} />
                        </div>
                        <RoField label="Units" value={detail.Units} />
                    </div>
                )}

                {/* Trader Details */}
                {detail?.TranNo && (loading.traders || traders.length > 0) && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-900 dark:text-blue-300 border-b border-gray-200 dark:border-gray-700">
                            Trader Details <span className="text-red-500">*</span>
                        </div>
                        {loading.traders ? (
                            <div className="p-3 text-center text-sm text-gray-500">Loading traders...</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th className={`${thCls} w-10 text-center`}>
                                                <input type="checkbox" checked={allTradersChecked} title="Tick all"
                                                    onChange={() => toggleAll(traders, allTradersChecked, setCheckedTraders)}
                                                    className="w-4 h-4 accent-blue-600 cursor-pointer" />
                                            </th>
                                            <th className={`${thCls} text-left`}>Trader Name</th>
                                            <th className={`${thCls} text-left`}>Email</th>
                                            <th className={`${thCls} text-left`}>Phone No</th>
                                            <th className={`${thCls} text-right`}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {traders.map((t, i) => (
                                            <tr key={i} className={checkedTraders[i] ? 'bg-green-50/60 dark:bg-green-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                                                <td className="px-2 py-1.5 text-center">
                                                    <input type="checkbox" checked={!!checkedTraders[i]}
                                                        onChange={(e) => setCheckedTraders((p) => ({ ...p, [i]: e.target.checked }))}
                                                        className="w-4 h-4 accent-blue-600 cursor-pointer" />
                                                </td>
                                                <td className="px-2 py-1.5 font-medium text-gray-800 dark:text-gray-200">{t.SupName}</td>
                                                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 break-all">{t.Supemail}</td>
                                                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{t.Supphone}</td>
                                                <td className={`px-2 py-1.5 text-right font-medium ${Number(t.SupAmt) > Number(t.Basic) ? 'text-red-600' : 'text-green-700 dark:text-green-400'}`}>
                                                    ₹ {t.SupRate}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Refer Link Details — rows without a link/rate are not shown */}
                {detail?.TranNo && (loading.links || visibleLinks.length > 0) && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-900 dark:text-blue-300 border-b border-gray-200 dark:border-gray-700">
                            Refer Link Details <span className="text-red-500">*</span>
                        </div>
                        {loading.links ? (
                            <div className="p-3 text-center text-sm text-gray-500">Loading links...</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className={`${thCls} w-10 text-center`}>
                                            <input type="checkbox" checked={allLinksChecked} title="Tick all"
                                                onChange={() => toggleAll(visibleLinks, allLinksChecked, setCheckedLinks)}
                                                className="w-4 h-4 accent-blue-600 cursor-pointer" />
                                        </th>
                                        <th className={`${thCls} text-left`}>Reference Link</th>
                                        <th className={`${thCls} text-right w-32`}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {visibleLinks.map((lk, i) => {
                                        const href = toHref(lk.Link);
                                        return (
                                            <tr key={i} className={checkedLinks[i] ? 'bg-green-50/60 dark:bg-green-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                                                <td className="px-2 py-1.5 text-center align-top">
                                                    <input type="checkbox" checked={!!checkedLinks[i]}
                                                        onChange={(e) => setCheckedLinks((p) => ({ ...p, [i]: e.target.checked }))}
                                                        className="w-4 h-4 accent-blue-600 cursor-pointer" />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <a href={href} target="_blank" rel="noopener noreferrer" title={lk.Link}
                                                       className="inline-flex items-start gap-1 text-blue-700 dark:text-blue-400 hover:underline break-all">
                                                        <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                        {lk.Linkshort || lk.Link}
                                                    </a>
                                                </td>
                                                <td className="px-2 py-1.5 text-right align-top">
                                                    <a href={href} target="_blank" rel="noopener noreferrer" title="Open link"
                                                       className={`font-medium hover:underline ${Number(lk.LinkAmt) > Number(lk.Basic) ? 'text-red-600' : 'text-green-700 dark:text-green-400'}`}>
                                                        ₹ {lk.LinkRate}
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={loading.remarks}
                    title="Approval History"
                />

                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have verified the item code details',
                        checkboxDescription: 'Confirm that the item name, HSN code, specification and pricing are correct',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Enter your verification remarks...',
                        commentRequired: true,
                        commentRows: 2,
                        commentMaxLength: 500,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        commentBorder: 'border-blue-200 dark:border-blue-700',
                    }}
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900" />
                        <span className="text-gray-600 dark:text-gray-400">Loading actions...</span>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-700 text-center text-sm text-red-600 dark:text-red-400">
                        ⚠️ Error loading actions: {statusError}
                    </div>
                ) : !hasActions || !enabledActions?.length ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-5 border border-yellow-200 dark:border-yellow-700 text-center text-sm text-yellow-700 dark:text-yellow-400">
                        ℹ️ No actions available for this record
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={loading.submit}
                        isVerified={isVerified}
                        comment={verificationComment}
                        showValidation={true}
                        excludeActions={['send back']}
                    />
                )}
            </div>
        );
    };


    // ── Main render ───────────────────────────────────────────────────────────

    return (
        <div className="space-y-3">
            <InboxHeader
                title={`${InboxTitle || 'Item Code Verification'} (${inbox.length})`}
                subtitle={ModuleDisplayName}
                itemCount={inbox.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Package}
                badgeText="Purchase Module"
                badgeCount={inbox.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by item name, code, row ID...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: loading.inbox,
                    error: errors.inbox,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No item codes pending verification.',
                        itemKey: 'Rowid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(true),
                    popupConfig: {
                        title: 'Item Code Verification',
                        icon: Package,
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: loading.detail,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Item Code Verification',
                        icon: Package,
                        selectedTitle: 'Item Code Verification',
                        emptyTitle: 'No Item Code Selected',
                        emptyMessage: 'Select an item code from the list to review and verify.',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxHeight: 'calc(100vh - 140px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyItemCode;
